import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import type { PetitionDetailsDataExperimentsQuery } from '../experiments.graphql';
import type { SpikingPetition } from '../spikingPetition';
import { getSpikingPetition } from '../spikingPetition';

type Experiment =
	PetitionDetailsDataExperimentsQuery['experiments']['petitionDetailsPageSpikingPetitionBannerExperiment'];

export type SpikingPetitionBanner = Readonly<{
	// Untreated: the banner treats the user itself, once `BannerSection` actually selects it.
	experiment: Experiment;
	petition: SpikingPetition;
}>;

type Params = {
	experiment: Experiment;
	// The current page's slug or ID, as in the URL.
	slugOrId: string;
	utilityContext: UtilityContext;
};

/**
 * Returns the banner data when the banner can render for every arm: the FCM enables the experiment, the
 * spiking petition is PUBLISHED, and the viewer is not already on it. Returns undefined otherwise.
 *
 * It does not treat the user. A higher-priority banner can still win in `BannerSection`, which this
 * normalizer doesn't know about, so treatment is left to the banner component, which only mounts once
 * it is selected.
 */
export async function normalizeSpikingPetitionBannerExperiment({
	experiment,
	slugOrId,
	utilityContext,
}: Params): Promise<SpikingPetitionBanner | undefined> {
	const { petitionId } = experiment.config;

	// An `enabledIf` failure (FC disabled, fetch error, not registered yet, …) surfaces as `fallback: true`.
	if (experiment.fallback || petitionId === undefined) return undefined;

	const lookup = await getSpikingPetition(utilityContext, { petitionId, currentSlugOrId: slugOrId });

	// Compare resolved ids, not the raw route param: the page resolves any slug or id format the API accepts.
	if (!lookup || lookup.petition.id === lookup.currentPetitionId) return undefined;

	return { experiment, petition: lookup.petition };
}
